import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getGateQr, regenerateGateQr } from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useAuth } from "../auth/useAuth";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";

export function GateQrPanel() {
  const { data: qr, loading, error, refresh } = useAdminResource(() => getGateQr(), []);
  const { showSuccess, showError } = useToast();
  const { session } = useAuth();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const fullUrl = qr?.register_path ? `${window.location.origin}${qr.register_path}` : "";

  async function handleRegenerate() {
    const ok = await confirm({
      title: "Generate a new gate QR?",
      message: "Any QR poster you already printed will stop working and must be reprinted.",
      confirmLabel: "Generate new QR",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await regenerateGateQr();
      showSuccess("New gate QR generated");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to regenerate");
    } finally {
      setBusy(false);
    }
  }

  function handlePrint() {
    if (!fullUrl) return;
    const company = session?.companyCode ?? "Visitor";
    const win = window.open("", "_blank", "width=720,height=900");
    if (!win) {
      showError("Allow pop-ups to print the QR poster.");
      return;
    }
    // A self-contained printable poster (inline SVG QR so no external assets).
    const svg = document.getElementById("gate-qr-svg")?.outerHTML ?? "";
    win.document.write(`<!doctype html><html><head><title>Gate QR - ${company}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;text-align:center;padding:48px;color:#111}
        h1{font-size:34px;margin:0 0 8px} h2{font-size:20px;color:#444;margin:0 0 28px;font-weight:500}
        .qr{display:inline-block;padding:20px;border:2px solid #111;border-radius:16px}
        p{font-size:16px;margin-top:26px;color:#333}
        .code{font-size:13px;color:#888;word-break:break-all;margin-top:10px}
      </style></head><body>
      <h1>${company} — Visitor Check-In</h1>
      <h2>Scan this QR with your phone camera to register your visit</h2>
      <div class="qr">${svg}</div>
      <p>After you submit, show your pass to security. Your host will approve your visit.</p>
      <div class="code">${fullUrl}</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`);
    win.document.close();
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Gate Self-Check-In QR</h2>
      </header>
      <p className="management-note">
        Print this QR and display it at your security gate. A visitor scans it with their phone,
        fills in their own details and picks who they're visiting, and gets a pass — which still
        needs the host's approval, just like a walk-in. Regenerate to invalidate an old printed
        poster.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      {loading ? (
        <p className="management-note">Loading…</p>
      ) : fullUrl ? (
        <div className="action-card" style={{ alignItems: "center", textAlign: "center" }}>
          <div style={{ background: "#fff", padding: "1rem", borderRadius: "0.75rem", display: "inline-block" }}>
            <QRCodeSVG id="gate-qr-svg" value={fullUrl} size={200} />
          </div>
          <p className="management-note" style={{ wordBreak: "break-all" }}>{fullUrl}</p>
          <div className="action-row" style={{ justifyContent: "center", gap: "0.75rem" }}>
            <button className="refresh-btn" type="button" onClick={handlePrint}>
              Print poster
            </button>
            <button className="refresh-btn danger-action-btn" type="button" onClick={() => void handleRegenerate()} disabled={busy}>
              {busy ? "Working..." : "Regenerate QR"}
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
